// What a caller hears when a mailbox picks up — every mailbox on the line, in one card.
//
// Kevin, 2026-09-02, with the two-level attendant on line 140 finally answering correctly: "that is
// working now. the voicemail greeting is missing."
//
// It was missing twice over. No greeting audio had been put on any of the four mailboxes — and
// nothing in the product could have said so, or done anything about it:
//
//   * EVERY GREETING WRITE WAS PORTAL-ONLY. The customer could record one from their own portal;
//     the operator building the flow could not, and on a new account there may be no portal user at
//     all — line 140 has none. So the person who builds a menu whose every prompt ends "leave a
//     message" had no way to give those mailboxes anything to say.
//   * GREETING STATE WAS READABLE ONE EXTENSION AT A TIME, through the agent proxy. So "did the
//     departments I just built get greetings?" was four calls and a mental join, and the Voicemail
//     tab — the one screen where those mailboxes are managed — was silent about it.
//
// SO THIS CARD IS BOTH THE REPORT AND THE CONTROL, which is the same shape the department switches
// beside it took, and for the same reason: naming a problem while offering no gesture for it leaves
// an operator nowhere to go.
//
// THE HOST DECIDES WHAT PLAYS. `present` comes from the mailbox's spool on the PBX, not from our
// record of what we last pushed. Those are different facts and this card shows both, because a
// greeting we believe we set which the host does not have is exactly what went wrong here.
//
// "NO GREETING" IS NEVER GUESSED. An unreachable host reads as "could not check", not as empty —
// telling an operator to re-record a greeting that is perfectly fine is the same class of lie as
// the badge that printed "On" over a host answering `novm`.

import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import RecordingPickerModal from '../shared/RecordingPickerModal.jsx';

// The three a caller can hear. `unavail` is the one that matters on nearly every line, so it is the
// row you get; the other two are behind a disclosure rather than tripling the card's height for
// cases most lines never use.
const LABELS = {
  unavail: 'When nobody answers',
  busy: 'When the line is busy',
  temp: 'Temporary — plays instead of the others',
};

// THE RECORDED NAME IS NOT ONE OF THOSE, which is why it is not in LABELS.
//
// Kevin, 2026-09-04, seeing "Recorded Name 0" on Lyons Vet's mailbox: "finish implementing it so
// other accounts may benefit." It had been settable by the agent all along and refused by Verso in
// four places, so the only way to record one was *97 from a handset.
//
// It gets its own row rather than a fourth greeting because a caller never hears it on its own:
// Asterisk speaks it inside its own sentences — the dial-by-name directory, and "…is unavailable"
// on a mailbox with no greeting. Put it in the greeting list and it reads as a fourth thing callers
// hear, which it is not.
const NAME_LABEL = 'Recorded name';

// Mirrors MAX_NAME_SEC in cloud/workers/src/routes/portal/greetingLibrary.js, which refuses the
// write. Checked here too so the picker says why BEFORE the push, rather than after a 400.
const MAX_NAME_SEC = 10;

// Seconds an operator can compare at a glance. 0.4 and 43.18 have to read as obviously different
// lengths, which "0.4s" and "43.18s" do and "less than a minute" twice would not.
function fmtSecs(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  if (n < 1) return `${n.toFixed(1)}s`;
  if (n < 60) return `${Math.round(n)}s`;
  return `${Math.floor(n / 60)}m ${String(Math.round(n % 60)).padStart(2, '0')}s`;
}

// WHY THE ERROR CARRIES THE AGENT'S OWN WORDS.
//
// The generic half — "the phone system would not accept that greeting" — is a dead end: an operator
// reading only that will try again, and again. The `detail` is the actionable half, and on the very
// first live attempt it was the whole answer: PBX-02 answered "voicemail spool is not writable by
// the agent — grant group-write on the mailbox directories", which is somebody's ten-second fix and
// nothing an operator could have guessed. The other one seen in practice is "no voicemail mailbox
// for extension … — enable voicemail first".
const messageFor = (e) => {
  const b = e?.body || {};
  const head = b.error || e?.message || 'That change failed.';
  return b.detail ? `${head} The phone system said: ${b.detail}` : head;
};

export default function MailboxGreetings({ line, canWrite, voicemailEnabled = null }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);   // { mailbox, type }
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const subscriberId = line.subscriberId ?? line.subscriber_id;

  const greetQ = useQuery({
    queryKey: ['line', String(line.id), 'greetings'],
    queryFn: () => api.get(`/lines/${line.id}/voicemail/greetings`),
    retry: false,
  });

  // WHICH DEPARTMENT EACH MAILBOX IS. A bare list of `27208934175`, `37208934175`, `47208934175`
  // tells an operator nothing about which one Sales callers reach, and getting the wrong greeting
  // onto the wrong department is the mistake this card exists to prevent. The ring groups already
  // know — each department's group has that mailbox's extension as its only member — so the name
  // comes from the card next door rather than a new field.
  const groupsQ = useQuery({
    queryKey: ['line', String(line.id), 'ring-groups'],
    queryFn: () => api.get(`/lines/${line.id}/ring-groups`),
    retry: false,
    staleTime: 60_000,
  });

  const labelFor = useMemo(() => {
    const map = {};
    for (const g of groupsQ.data?.groups || []) {
      const members = g.verso?.members || g.host?.members || [];
      for (const m of members) {
        // Only a DEPARTMENT name is worth showing. The line's own group is the line, and repeating
        // the line's name against its own mailbox is noise.
        if (g.role === 'department' && g.name) map[String(m)] = g.name;
      }
    }
    return map;
  }, [groupsQ.data]);

  const { data: lib } = useQuery({
    queryKey: ['subscriber', String(subscriberId), 'recordings'],
    queryFn: () => api.get(`/subscribers/${subscriberId}/recordings`),
    enabled: subscriberId != null,
    retry: false,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['line', String(line.id), 'greetings'] });
    qc.invalidateQueries({ queryKey: ['subscriber', String(subscriberId), 'voicemails'] });
  };

  const remove = async (mailbox, type) => {
    setBusy(`${mailbox}:${type}`);
    setError(null);
    try {
      const r = await api.delete(`/subscribers/${subscriberId}/voicemail/${mailbox}/greeting/${type}`);
      if (r?.error) throw new Error(r.error);
      refresh();
    } catch (e) {
      setError(messageFor(e));
    } finally {
      setBusy(null);
    }
  };

  if (greetQ.isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-5 text-sm text-gray-500">
        Loading greetings…
      </div>
    );
  }

  const mailboxes = greetQ.data?.mailboxes || [];
  if (!mailboxes.length && !greetQ.error) return null;

  // IS THERE A MAILBOX TO PUT A GREETING ON?
  //
  // Found on the first live attempt: 17208934175 — the line's OWN extension — answered 404 "no
  // voicemail mailbox for extension … enable voicemail first". The card had offered a Set button
  // for it anyway, so the only way to learn was to pick a recording and be refused. That is
  // expected on this line, where callers reach the menu rather than the line's own mailbox, and it
  // will be expected on any line whose voicemail is deliberately off.
  //
  // The switches above already read this from the host, so the answer is passed in rather than
  // asked for a second time — two reads of "does this mailbox exist" is two things to disagree.
  // `null` (not asked) offers the button: refusing on an unknown is worse than a clear error.
  const hasMailbox = (mb) => (voicemailEnabled ? voicemailEnabled[mb.sip_account_id] !== false : true);

  // THE RECORDED NAME'S OWN ROW. Same shape as a greeting row so it does not look bolted on, and
  // deliberately different words: what it is FOR, not what a caller hears, because a caller never
  // hears it in its own right.
  const nameRow = (mb) => {
    const n = mb.recorded_name || {};
    const key = `${mb.mailbox}:name`;
    const working = busy === key;
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 py-1">
        <div className="min-w-0">
          <div className="text-xs text-gray-500">{NAME_LABEL}</div>
          <div className="text-sm">
            {n.present === null ? (
              <span className="text-gray-500">Could not check</span>
            ) : n.present && n.matches_record === false ? (
              // The same divergence the greeting rows report, for the same reason.
              <span className="text-amber-700">
                The mailbox holds a different recording from{' '}
                <span className="font-medium">{n.recording?.name}</span>
                {n.duration_sec != null && <> — {fmtSecs(n.duration_sec)} on the phone system</>}
                . Set it again to replace it.
              </span>
            ) : n.present ? (
              <span className="text-gray-900">
                {n.recording?.name
                  ? <>Recorded as <span className="font-medium">{n.recording.name}</span></>
                  : 'Recorded on the phone, not from the library'}
              </span>
            ) : (
              // NOT A FAULT, and this is the sentence that stops it reading as one. Lyons Vet has
              // no recorded name and it changes nothing for them: with a greeting on the mailbox
              // the name is never reached.
              <span className="text-gray-600">
                Not recorded — the phone system reads the extension digits instead
              </span>
            )}
          </div>
          <p className="text-[11px] text-gray-400">
            Spoken by the phone system itself, in a dial-by-name directory and in “…is unavailable”
            on a mailbox with no greeting. Just the name, under {MAX_NAME_SEC}s.
          </p>
        </div>
        {canWrite && (
          <div className="flex shrink-0 items-center gap-3 text-xs">
            <button
              type="button"
              disabled={working}
              onClick={() => { setError(null); setEditing({ mailbox: mb.mailbox, type: 'name' }); }}
              className="cursor-pointer text-blue-600 underline hover:no-underline disabled:cursor-wait"
            >
              {n.present ? 'Change' : 'Record a name'}
            </button>
            {n.present === true && (
              <button
                type="button"
                disabled={working}
                onClick={() => remove(mb.mailbox, 'name')}
                className="cursor-pointer text-gray-500 underline hover:no-underline disabled:cursor-wait"
              >
                Remove
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const slotRow = (mb, g) => {
    const key = `${mb.mailbox}:${g.type}`;
    const working = busy === key;
    return (
      <div key={g.type} className="flex flex-wrap items-center justify-between gap-2 py-1">
        <div className="min-w-0">
          <span className="text-xs text-gray-500">{LABELS[g.type]}</span>
          <div className="text-sm">
            {/* THE THREE ANSWERS, KEPT APART. Present with a name we recorded; present from
                somewhere else (a handset, or before this feature existed); and absent. The middle
                one used to be indistinguishable from the last, so a greeting recorded on a phone
                looked like no greeting at all. */}
            {g.present === null ? (
              <span className="text-gray-500">Could not check</span>
            ) : g.instructions_only ? (
              // OUR OWN SILENCE, WHICH IS THE ABSENCE OF A GREETING. Kevin, 2026-09-02: "if no
              // greeting is selected, the voicemail should bypass the default greeting and just
              // play the instructions." The host answers `present: true` because there is a file
              // on the slot; calling that a greeting would hide the mailboxes that still need one.
              // Said as what the caller gets, so an operator can decide whether it needs changing.
              <span className="text-gray-600">
                No greeting — callers hear only “record your message after the tone”
              </span>
            ) : g.present && g.matches_record === false ? (
              // THE FILE ON THE MAILBOX IS NOT THE ONE WE CLAIM IS ON IT, and this line exists
              // because the branch below stated the claim as fact.
              //
              // Lyons Veterinary Clinic, 2026-09-04. Taylor Smith: "Why are the personal recorded
              // greetings for Lyons Vet voicemails not working and using a generic default
              // instead?" The slot claimed a 43.18s greeting that had genuinely been on that host
              // in August; the mailbox held 0.4 seconds. This card said "Playing Unavailable
              // greeting (13038236767)" and gave the operator no reason to look further, which is
              // how a customer's greeting stayed broken while the screen for it read fine.
              //
              // Named as what the caller gets, with both measurements, because "mismatch" alone
              // does not tell an operator whether to re-push or to go and listen.
              <span className="text-amber-700">
                Callers are not hearing{' '}
                <span className="font-medium">{g.recording?.name}</span>. The mailbox holds a
                different recording
                {g.duration_sec != null && <> — {fmtSecs(g.duration_sec)} on the phone system</>}
                {g.recording?.duration_sec != null && <>, against {fmtSecs(g.recording.duration_sec)} in the library</>}
                . Set it again to replace it.
              </span>
            ) : g.present ? (
              <span className="text-gray-900">
                {g.recording?.name
                  ? <>Playing <span className="font-medium">{g.recording.name}</span>
                      {g.recording.deleted && (
                        <span className="text-gray-500"> (no longer in the library)</span>
                      )}</>
                  : 'A greeting is set — recorded on the phone, not from the library'}
              </span>
            ) : !hasMailbox(mb) ? (
              // Not a missing greeting — a missing mailbox. Calling it "no greeting" would send an
              // operator looking for a recording to fix something a recording cannot fix.
              <span className="text-gray-500">No mailbox, so there is nothing to record</span>
            ) : (
              <span className="text-amber-700">
                {/* WHAT THE CALLER ACTUALLY GETS, not "empty". An operator cannot judge whether
                    this needs fixing without knowing what happens instead.
                    "The default system message" was too vague to act on, and Kevin named why:
                    "if the unavailable greeting gets removed, it would fall back on the recorded
                    name." With the slot genuinely empty Asterisk plays vm-theperson — the recorded
                    name if there is one, and otherwise the mailbox number read out one digit at a
                    time. Those are very different things to hear, and only one of them is worth
                    fixing in a hurry. */}
                {/* `present` is TRI-STATE and null is "we could not look" — which a plain
                    ternary read as false, claiming the digits get read out on a host nobody had
                    successfully asked. Caught by a test, and it is the same rule as everywhere
                    else on this card: unknown is drawn as unknown. */}
                {g.type === 'unavail' && typeof mb.recorded_name?.present === 'boolean'
                  ? (mb.recorded_name.present
                    ? 'No greeting — the system says the recorded name, then “is unavailable”'
                    : 'No greeting — the system reads the number out digit by digit')
                  : 'No greeting — callers hear the default system message'}
              </span>
            )}
            {/* WE PUSHED ONE AND THE HOST DOES NOT HAVE IT. Two sources of truth disagreeing is
                worth a sentence rather than a shrug: our record says this mailbox has a greeting
                and the mailbox says otherwise, so re-assigning is the fix. */}
            {g.present === false && g.recording?.name && (
              <div className="text-xs text-amber-700">
                Verso recorded <span className="font-medium">{g.recording.name}</span> here, but the
                phone system does not have it. Set it again.
              </div>
            )}
          </div>
        </div>
        {canWrite && !hasMailbox(mb) ? (
          <span className="shrink-0 text-xs text-gray-500">
            Voicemail is off for this extension
          </span>
        ) : canWrite && (
          <div className="flex shrink-0 items-center gap-3">
            <button
              type="button"
              disabled={working || !mb.readable}
              onClick={() => {
                setError(null);
                setEditing({
                  mailbox: mb.mailbox,
                  type: g.type,
                  // A real one, not our silence — silence is what "instructions only" already is,
                  // so choosing it again has nothing to warn about.
                  hasGreeting: g.present === true && !g.instructions_only,
                });
              }}
              className="text-xs font-medium text-blue-700 underline hover:text-blue-900 disabled:opacity-50"
            >
              {/* KEYED ON WHETHER THERE IS A GREETING, not on whether the slot holds a file. Our
                  instructions-only silence makes `present` true with nothing to change, and
                  "Change" over "No greeting" is a contradiction an operator has to resolve. */}
              {g.present && !g.instructions_only ? 'Change' : 'Set greeting'}
            </button>
            {g.present === true && !g.instructions_only && (
              <button
                type="button"
                disabled={working}
                onClick={() => remove(mb.mailbox, g.type)}
                className="text-xs font-medium text-gray-500 underline hover:text-gray-800 disabled:opacity-50"
              >
                {working ? 'Removing…' : 'Remove'}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-900">Voicemail greetings</h3>
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="text-xs font-medium text-blue-700 underline hover:text-blue-900"
        >
          {showAll ? 'Show only the main greeting' : 'Busy and temporary greetings'}
        </button>
      </div>
      <p className="mt-1 text-sm text-gray-600">
        What a caller hears before the beep. A mailbox with no greeting of its own goes straight to
        the instructions — callers never hear the phone system read the extension number out.
      </p>

      {greetQ.error && (
        <p className="mt-3 text-sm text-amber-700">
          Could not read greetings from the phone system, so this list may be out of date.
        </p>
      )}
      {error && <p className="mt-3 text-sm text-red-700">{error}</p>}

      <ul className="mt-3 divide-y divide-gray-100">
        {mailboxes.map((mb) => {
          const shown = mb.greetings.filter((g) => showAll || g.type === 'unavail');
          return (
            <li key={mb.mailbox} className="py-2">
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-sm text-gray-900">{mb.mailbox}</span>
                {labelFor[mb.mailbox] && (
                  <span className="text-xs text-gray-600">{labelFor[mb.mailbox]}</span>
                )}
              </div>
              {/* Per mailbox, not for the card: one mailbox that cannot be read must not hide the
                  greetings on its siblings. */}
              {!mb.readable && (
                <p className="text-xs text-gray-500">
                  The phone system could not be asked about this mailbox.
                </p>
              )}
              <div className="mt-1">{shown.map((g) => slotRow(mb, g))}</div>
              {/* Only under the disclosure, with the other two rows most lines never touch — the
                  main greeting is what an operator came here for. */}
              {showAll && mb.recorded_name && hasMailbox(mb) && (
                <div className="mt-1">{nameRow(mb)}</div>
              )}
            </li>
          );
        })}
      </ul>

      {editing && (
        <RecordingPickerModal
          title="Choose a recording"
          subtitle={editing.type === 'name'
            ? `For ${editing.mailbox} — recorded name`
            : `For ${editing.mailbox} — ${LABELS[editing.type].toLowerCase()}`}
          // THIS CUSTOMER'S RECORDINGS AND ONLY THEIRS, and any kind of them. Kevin, 2026-09-02: "I
          // shouldn't see SML recordings or IVRs, etc… when I am configuring Lyons Communications
          // line." The phone system's catalogue is host-wide and unowned, so it is not offered here
          // either.
          //
          // A recording uploaded as a MENU PROMPT is offered too, deliberately: `kind` is a label on
          // one library, not a partition, and "no one is available to take your call" is exactly as
          // true from the mailbox as from the menu that sent the caller to it. Making an operator
          // upload the same audio twice under two labels would be busywork.
          recordings={lib == null ? null : [
            // "JUST PLAY THE INSTRUCTIONS" AS A CHOICE IN THE LIST.
            //
            // Kevin, 2026-09-02: "a number of mailboxes don't have a greeting, so this needs to be
            // a selectable option when setting up voicemail mailbox greetings." It arrived as
            // automatic behaviour when a mailbox is switched on, which does nothing for the
            // mailboxes that already exist — and nobody re-enables voicemail across the fleet to
            // change what a caller hears. So it belongs here, where the decision is made.
            //
            // FIRST, because on a mailbox with no greeting it is the likely answer, and because a
            // choice buried under eight recordings is not a choice anybody finds.
            //
            // `source` is the picker's own passthrough — it exists precisely so a caller can tell
            // its options apart on the way back out — so this needs no new prop.
            // NOT OFFERED FOR THE RECORDED NAME. A silence on that slot does not mean "no name" —
            // it means the directory announces a moment of nothing. The server refuses it; not
            // listing it is how an operator never has to find that out from a 400.
            ...(editing.type === 'name'
              ? []
              : [{ id: 'instructions', name: 'Just play the instructions (no greeting)', source: 'instructions' }]),
            ...(lib.items || []).map((r) => ({
              id: r.id, name: r.name, source: 'library', disabled: r.multi_part,
            })),
          ]}
          subscriberId={subscriberId}
          kind="greeting"
          // SAID BEFORE THE PUSH, not after a 400. Lyons Vet's own greeting is 43.18s and sits in
          // this very list, one row from the name slot — allowed through, the directory reads all
          // 43 seconds of it to every caller who lands there.
          onPreview={editing.type !== 'name' ? undefined : (opt) => {
            const item = (lib?.items || []).find((r) => String(r.id) === String(opt.id));
            const secs = Number(item?.duration_sec);
            if (Number.isFinite(secs) && secs > MAX_NAME_SEC) {
              return { blockers: [{ id: opt.id, why: `${fmtSecs(secs)} is too long for a recorded name — the phone system speaks it inside its own sentences. Record just the name, under ${MAX_NAME_SEC}s.` }] };
            }
            return {};
          }}
          onSelect={async (opt) => {
            // REPLACING A REAL GREETING WITH SILENCE IS ASKED ABOUT FIRST. The server does it
            // without argument for an explicit choice — an operator who picked this has said what
            // they want — so the check that the customer's recording is about to stop playing has
            // to be here. Only where there IS one: confirming over "No greeting" is noise.
            if (opt.source === 'instructions' && editing.hasGreeting
                && !window.confirm(
                  `Callers will stop hearing the greeting on ${editing.mailbox} and get the `
                  + 'instructions instead. Replace it?')) {
              return;
            }
            const r = await api.put(
              `/subscribers/${subscriberId}/voicemail/${editing.mailbox}/greeting/${editing.type}`,
              opt.source === 'instructions' ? { instructions_only: true } : { recording_id: opt.id },
            );
            // The composed text goes in `body.error`, which is what the picker prefers — putting
            // it only in the Error's message would leave the modal showing the generic half and
            // dropping the agent's reason, which is the half worth reading.
            if (r?.error) {
              const message = messageFor({ body: r });
              throw Object.assign(new Error(message), { body: { ...r, error: message } });
            }
            refresh();
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
